import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useCustomSnackbar from "../../../../../hooks/useCustomSnackbar";
import { setActiveModule } from "../../../../../redux/action/ActiveModule-actions";
import { setFilters } from "../../../../../redux/action/Filters-action";
import {
  incrementMapReloadCount,
  setShowModules,
  setUserRole,
} from "../../../../../redux/action/MapView-action";
import { ConfigContext } from "../../../../../utils/ConfigContext";
import { getConfig, ROLE_EDITOR } from "../../../../../utils/helper";
import { EditIcon } from "../../../../Icons";
import { Tooltip } from "../../../../UI";
import {
  StyledIconContainer,
  StyledSwitchContainer,
  StyledSwitchHandle,
  StyledSwitchWrapper,
  StyledTextContainer,
  StyledTextWrapper,
} from "./RoleSwitch-styled";

const RoleSwitch = ({ expanded }) => {
  const [openSnackbar] = useCustomSnackbar();
  const { t } = useTranslation("common");
  const { config, setConfig } = useContext(ConfigContext);
  const dispatch = useDispatch();

  const viewerRole = useSelector((state) => state.mapViewReducer.role);
  const activeModule = useSelector((state) => state.activeModule);

  const isEditor = useMemo(() => {
    return config.role === ROLE_EDITOR;
  }, [config]);

  const handleSwitchChange = useCallback(
    (checked) => {
      if (isEditor === checked) return;

      const url = new URL(window.location.href);
      dispatch(setFilters({})); // Reset filters on role change
      const currModule = activeModule;
      const role = checked ? ROLE_EDITOR : "viewer";
      if (role === ROLE_EDITOR) {
        url.searchParams.set("role", ROLE_EDITOR);
        window.history.replaceState({}, document.title, url.toString());
      }

      dispatch(setUserRole(role));
      const conf = getConfig(t, openSnackbar);

      if (
        Array.isArray(conf.activeModules) &&
        conf.activeModules.includes(currModule)
      ) {
        dispatch(setActiveModule(currModule));
        dispatch(setShowModules(false));
      } else {
        dispatch(setShowModules(true));
      }

      // setConfig(null);
      setConfig(conf);
      setTimeout(() => {
        if (role !== ROLE_EDITOR) {
          url.searchParams.delete("role");
          window.history.replaceState({}, document.title, url.toString());
        }
        dispatch(incrementMapReloadCount());
      }, 100);
    },
    [activeModule, isEditor]
  );

  const editorModeText = t("screen.widget.Editor.switch.editorMode");
  const publicViewerModeText = t(
    "screen.widget.Editor.switch.publicViewerMode"
  );
  const information = t("screen.widget.Editor.switch.information");

  return (
    <Tooltip
      content={information}
      placement="right"
      offset={16}
      blurred
      showOnHover={expanded}
      showChildrenFallback
    >
      <StyledSwitchWrapper>
        <StyledSwitchContainer>
          <StyledSwitchHandle isEditor={isEditor} />
          <Tooltip
            placement="right"
            content={publicViewerModeText}
            showOnHover={!expanded}
            offset={20}
            blurred
          >
            <StyledIconContainer
              onClick={() => handleSwitchChange(false)}
              isSelected={!isEditor}
              bottom
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 576 512"
                height="16px"
                width="16px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M288 144a110.94 110.94 0 0 0-31.24 5 55.4 55.4 0 0 1 7.24 27 56 56 0 0 1-56 56 55.4 55.4 0 0 1-27-7.24A111.71 111.71 0 1 0 288 144zm284.52 97.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400c-98.65 0-189.09-55-237.93-144C98.91 167 189.34 112 288 112s189.09 55 237.93 144C477.1 345 386.66 400 288 400z" />
              </svg>
            </StyledIconContainer>
          </Tooltip>
          <Tooltip
            placement="right"
            content={editorModeText}
            offset={20}
            showOnHover={!expanded}
            blurred
          >
            <StyledIconContainer
              onClick={() => handleSwitchChange(true)}
              isSelected={isEditor}
              top
            >
              <EditIcon fill="currentColor" height="16px" width="16px" />
            </StyledIconContainer>
          </Tooltip>
        </StyledSwitchContainer>
        <StyledTextContainer expanded={expanded}>
          <StyledTextWrapper onClick={() => handleSwitchChange(false)}>
            <span>{publicViewerModeText}</span>
          </StyledTextWrapper>
          <StyledTextWrapper onClick={() => handleSwitchChange(true)}>
            <span>{editorModeText}</span>
          </StyledTextWrapper>
        </StyledTextContainer>
      </StyledSwitchWrapper>
    </Tooltip>
  );
};

export default RoleSwitch;
